import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ReviewStatus = 'pending' | 'approved' | 'rejected';

export interface AdminReview {
  id: string;
  product_id: string;
  user_id: string | null;
  rating: number;
  title: string | null;
  content: string | null;
  verified_purchase: boolean;
  helpful_count: number;
  is_featured: boolean;
  status: ReviewStatus;
  reviewer_name: string;
  reviewer_email: string | null;
  created_at: string;
  updated_at: string;
  product_name?: string;
  product_image?: string;
  product_slug?: string;
  reviewer_avatar?: string;
}

export interface ReviewStats {
  total_reviews: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  average_rating: number;
  approval_rate: number;
}

interface UseAdminReviewsOptions {
  status?: ReviewStatus | 'all';
  searchQuery?: string;
  productId?: string;
  rating?: number;
  verifiedOnly?: boolean;
}

export const useAdminReviews = (options: UseAdminReviewsOptions = {}) => {
  return useQuery({
    queryKey: ['admin-reviews', options],
    queryFn: async (): Promise<AdminReview[]> => {
      let query = supabase
        .from('product_reviews')
        .select(`
          *,
          products:product_id (
            name,
            image_url,
            url_slug
          )
        `)
        .order('created_at', { ascending: false });

      // Apply status filter
      if (options.status && options.status !== 'all') {
        query = query.eq('status', options.status);
      }

      // Apply product filter
      if (options.productId) {
        query = query.eq('product_id', options.productId);
      }

      // Apply rating filter
      if (options.rating) {
        query = query.eq('rating', options.rating);
      }

      // Apply verified purchase filter
      if (options.verifiedOnly) {
        query = query.eq('verified_purchase', true);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching admin reviews:', error);
        throw error;
      }

      // Transform the data to match AdminReview interface
      const reviews: AdminReview[] = (data || []).map((review: any) => ({
        id: review.id,
        product_id: review.product_id,
        user_id: review.user_id,
        rating: review.rating,
        title: review.title,
        content: review.content,
        verified_purchase: review.verified_purchase,
        helpful_count: review.helpful_count,
        is_featured: review.is_featured,
        status: review.status,
        reviewer_name: review.reviewer_name,
        reviewer_email: review.reviewer_email,
        created_at: review.created_at,
        updated_at: review.updated_at,
        product_name: review.products?.name,
        product_image: review.products?.image_url,
        product_slug: review.products?.url_slug,
        reviewer_avatar: undefined, // No longer fetching from profiles
      }));

      // Apply search filter on client side
      if (options.searchQuery) {
        const query = options.searchQuery.toLowerCase();
        return reviews.filter(review => 
          review.product_name?.toLowerCase().includes(query) ||
          review.reviewer_name.toLowerCase().includes(query) ||
          review.title?.toLowerCase().includes(query)
        );
      }

      return reviews;
    },
  });
};

export const useReviewStats = () => {
  return useQuery({
    queryKey: ['review-stats'],
    queryFn: async (): Promise<ReviewStats> => {
      const { data, error } = await supabase
        .from('product_reviews')
        .select('id, status, rating');

      if (error) {
        console.error('Error fetching review stats:', error);
        throw error;
      }

      const reviews = data || [];
      const total = reviews.length;
      const pending = reviews.filter(r => r.status === 'pending').length;
      const approved = reviews.filter(r => r.status === 'approved').length;
      const rejected = reviews.filter(r => r.status === 'rejected').length;
      const avgRating = total > 0 
        ? reviews.reduce((sum, r) => sum + r.rating, 0) / total 
        : 0;
      const approvalRate = total > 0 
        ? (approved / total) * 100 
        : 0;

      return {
        total_reviews: total,
        pending_count: pending,
        approved_count: approved,
        rejected_count: rejected,
        average_rating: Number(avgRating.toFixed(2)),
        approval_rate: Number(approvalRate.toFixed(1)),
      };
    },
  });
};
